import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, MenuItem, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { BankAccountService } from '../../services/bankAccount.service';
import { BankReconciliationService } from '../../services/bankReconciliation.service';
import { BankAccount } from '../../interfaces/bank/bankAccount.interface';
import { BankLedgerLine, UnreconciledReport as UnreconciledReportDTO } from '../../interfaces/bank/bankReconciliation.interface';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { humanizeEnum } from '../../utils/format';
import { parseContentDispositionFilename } from '../../utils/contentDisposition';

const money = (n: number | null | undefined): string =>
  (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = (): string => toLocalIsoDate();

export { parseContentDispositionFilename };

/** Summary tile: label + a money value, used for the five report totals. */
const SummaryTile: React.FC<{ label: string; value: number; testId: string }> = ({ label, value, testId }) => (
  <Card variant="outlined" sx={{ flex: '1 1 200px', minWidth: 200 }} data-testid={testId}>
    <CardContent>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h6">{money(value)}</Typography>
    </CardContent>
  </Card>
);

/** Bank reconciliation "unreconciled report" page (T10). Point-in-time as-of-date tie-out. */
const UnreconciledReport: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!;

  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [bankAccountId, setBankAccountId] = useState('');
  const [asOf, setAsOf] = useState(today());
  const [report, setReport] = useState<UnreconciledReportDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await BankAccountService.listByInstitution(institutionId);
        setAccounts(res);
        if (res.length > 0) setBankAccountId(res[0].id);
      } catch (e) {
        toast.showApiError(e, 'Failed to load bank accounts');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    if (!bankAccountId) return;
    try {
      setLoading(true);
      const data = await BankReconciliationService.getUnreconciledReport(bankAccountId, asOf);
      setReport(data);
    } catch (e) {
      toast.showApiError(e, 'Failed to load unreconciled report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (bankAccountId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankAccountId, asOf]);

  const outstandingLedgerItems = useMemo<BankLedgerLine[]>(() => {
    if (!report) return [];
    return [...report.depositsInTransit, ...report.outstandingWithdrawals];
  }, [report]);

  const handleDownload = async () => {
    if (!bankAccountId) return;
    try {
      setDownloading(true);
      const response = await BankReconciliationService.downloadUnreconciledXlsxResponse(bankAccountId, asOf);
      const filename = parseContentDispositionFilename(
        response.headers['content-disposition'],
        `unreconciled-${bankAccountId}-${asOf}.xlsx`,
      );
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.showApiError(e, 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box data-help-page="banking.unrecReport.page" sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Unreconciled Report</Typography>

      <Box data-help-section="banking.unrecReport.filter" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <TextField data-help="banking.unrecReport.account"
          select
          size="small"
          label="Bank Account"
          value={bankAccountId}
          onChange={e => setBankAccountId(e.target.value)}
          sx={{ flex: '1 1 260px', maxWidth: 320 }}
          data-testid="unreconciled-report-account"
        >
          {accounts.map(a => (
            <MenuItem key={a.id} value={a.id}>{a.bankName} — {a.accountNumber}</MenuItem>
          ))}
        </TextField>
        <TextField data-help="banking.unrecReport.asOf"
          size="small"
          label="As of"
          type="date"
          value={asOf}
          onChange={e => setAsOf(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ flex: '1 1 260px', maxWidth: 320 }}
        />
        <Button data-help="banking.unrecReport.download"
          variant="contained"
          startIcon={<FileDownloadIcon />}
          onClick={handleDownload}
          disabled={!report || downloading}
          data-testid="unreconciled-report-download"
        >
          Download XLSX
        </Button>
      </Box>

      {loading && <CircularProgress size={24} />}

      {!loading && report && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {report.bankName} — {report.accountNumber} · GL {report.glCashAccountCode} {report.glCashAccountName}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <SummaryTile label="Book Balance" value={report.bookBalance} testId="tile-book-balance" />
            <SummaryTile label="Deposits in Transit" value={report.depositsInTransitTotal} testId="tile-deposits-in-transit" />
            <SummaryTile label="Outstanding Withdrawals" value={report.outstandingWithdrawalsTotal} testId="tile-outstanding-withdrawals" />
            <SummaryTile label="Outstanding Total" value={report.outstandingTotal} testId="tile-outstanding-total" />
            <SummaryTile label="Adjusted Book Balance" value={report.adjustedBookBalance} testId="tile-adjusted-book-balance" />
          </Box>

          <Typography variant="subtitle1" sx={{ mb: 1 }}>Outstanding Ledger Items</Typography>
          <TableContainer data-help-section="banking.unrecReport.ledgerTable" component={Paper} sx={{ mb: 3 }}>
            <Table size="small" data-testid="outstanding-ledger-table">
              <TableHead>
                <TableRow>
                  <TableCell data-help="banking.unrecReport.ledger.col.entry">Entry #</TableCell>
                  <TableCell data-help="banking.unrecReport.ledger.col.date">Date</TableCell>
                  <TableCell data-help="banking.unrecReport.ledger.col.description">Description</TableCell>
                  <TableCell data-help="banking.unrecReport.ledger.col.type">Type</TableCell>
                  <TableCell data-help="banking.unrecReport.ledger.col.amount" align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {outstandingLedgerItems.map(line => (
                  <TableRow key={line.journalEntryLineId}>
                    <TableCell>{line.entryNumber}</TableCell>
                    <TableCell>{formatDate(line.date)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'normal', minWidth: 140 }}>{line.description ?? '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={humanizeEnum(line.entryType)}
                        color={line.entryType === 'DEBIT' ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell align="right">{money(line.amount)}</TableCell>
                  </TableRow>
                ))}
                {outstandingLedgerItems.length === 0 && (
                  <TableRow><TableCell colSpan={5} align="center">No outstanding ledger items.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle1" sx={{ mb: 1 }}>Unmatched Statement Lines</Typography>
          <TableContainer data-help-section="banking.unrecReport.stmtTable" component={Paper}>
            <Table size="small" data-testid="unmatched-statement-lines-table">
              <TableHead>
                <TableRow>
                  <TableCell data-help="banking.unrecReport.stmt.col.date">Date</TableCell>
                  <TableCell data-help="banking.unrecReport.stmt.col.description">Description</TableCell>
                  <TableCell data-help="banking.unrecReport.stmt.col.reference">Reference</TableCell>
                  <TableCell data-help="banking.unrecReport.stmt.col.amount" align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.unmatchedStatementLines.map(line => (
                  <TableRow key={line.id}>
                    <TableCell>{formatDate(line.lineDate)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'normal', minWidth: 140 }}>{line.description ?? '—'}</TableCell>
                    <TableCell>{line.externalRef ?? '—'}</TableCell>
                    <TableCell align="right">{money(line.amount)}</TableCell>
                  </TableRow>
                ))}
                {report.unmatchedStatementLines.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center">No unmatched statement lines.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default UnreconciledReport;
